import { useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WizardStateContext from 'components/Wizards/shared/context/wizardstate';
import { useAIFeatures, AI_FEATURE_CONFIG_KEYS } from 'hooks/useAIFeatures';
import { AiExpandableSinglePrompt } from 'ts-components/AiExpandableSinglePrompt';
import { FLOW_TYPE, TASK_TYPE } from 'ts-components/Conversation';
import { GUARD_RAIL_CODES } from 'ts-components/Conversation';
import { toastVariant, useToast } from 'components/ToastProvider';
import { layers } from '__app/spacing';
import { editableDashlet } from 'pages/Dashboard/util';
import { preserveDashletStyles } from './utils';

const AUTO_OPEN_DELAY = 500;

const AIEditSection = ({
  inModal,
  autoOpen = false,
  isEditing,
  homepage,
  buildConfigFromState,
  willModalExpand = false,
  entityConfig,
  targetObjectId,
  dashboardId,
  onSuccessCallback,
  oneShotOnly,
}) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const { state, setIsGenerating, mergeState } = useContext(
    WizardStateContext.Context
  );
  const { aiFeaturesEnabled } = useAIFeatures(
    AI_FEATURE_CONFIG_KEYS.dashlet_builder
  );

  const { name: entityName, blockType, messages } = entityConfig;

  const config = useMemo(
    () => buildConfigFromState(state),
    [buildConfigFromState, state]
  );

  const contextValue = useMemo(
    () => ({
      ...(config ? { existing_config: config } : {}),
      ...(isEditing ? { mode: 'update' } : { mode: 'create' }),
      ...(homepage ? { block_type: blockType } : {}),
    }),
    [config, isEditing, homepage, blockType]
  );

  const extractGenerationResult = useCallback((response) => {
    const runMetadata = response.run_metadata;
    const guardrailCode = runMetadata?.guardrail_code;
    const guardrailMessage = runMetadata?.guardrail_message;
    const sessionId = response.session_id || runMetadata?.session_id;
    const generatedConfig = runMetadata?.dashlet_config;

    return {
      hasResult: !!generatedConfig,
      sessionId,
      guardrailCode,
      guardrailMessage,
    };
  }, []);

  const handleSuccess = useCallback(
    (flowStatusData) => {
      const { run_metadata = {} } = flowStatusData;
      const generatedConfig = run_metadata?.dashlet_config;
      const guardrailCode = run_metadata?.guardrail_code;
      const guardrailMessage = run_metadata?.guardrail_message;

      if (generatedConfig) {
        const generatedConfigState = editableDashlet(generatedConfig, t);

        const stateWithPreservedStyles = preserveDashletStyles(
          generatedConfigState,
          state
        );
        mergeState(stateWithPreservedStyles);
        onSuccessCallback?.(generatedConfig);
        if (guardrailCode && guardrailMessage) {
          showToast({
            message: guardrailMessage,
            variant:
              guardrailCode === GUARD_RAIL_CODES.REQUIRES_REVIEW
                ? toastVariant.ALERT
                : toastVariant.FAILURE,
            autohide: false,
          });
        } else {
          showToast({
            message: messages.success(t),
            variant: toastVariant.SUCCESS,
          });
        }
      }
    },
    [mergeState, t, showToast, state, onSuccessCallback, messages]
  );

  const onOneShotSuccess = useCallback(
    (flowStatusData) => handleSuccess(flowStatusData),
    [handleSuccess]
  );

  const onChatSessionSuccess = useCallback(
    (flowStatusData) => handleSuccess(flowStatusData),
    [handleSuccess]
  );

  const onGenerationFailure = useCallback(
    (error) => {
      const usageError = error?.response?.data?.run_context?.usage;
      if (usageError && usageError[0]) {
        showToast({
          message: usageError[0],
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: messages.failure(t),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [showToast, t, messages]
  );

  if (!aiFeaturesEnabled) {
    return null;
  }

  return (
    <AiExpandableSinglePrompt
      flowType={FLOW_TYPE.dashlet_builder_inline}
      taskType={TASK_TYPE.DASHLET_PLAN_GENERATION}
      context={contextValue}
      onOneShotSuccess={onOneShotSuccess}
      onChatSessionSuccess={onChatSessionSuccess}
      onGenerationFailure={onGenerationFailure}
      entityName={entityName(t)}
      extractGenerationResult={extractGenerationResult}
      autoOpen={autoOpen}
      autoOpenDelay={willModalExpand ? AUTO_OPEN_DELAY : 0}
      setIsGenerating={setIsGenerating}
      inModal={inModal}
      dashboardId={dashboardId}
      customObjectId={targetObjectId}
      buttonLabel={t('SETUP WITH AI')}
      popoverProps={{
        placement: 'bottom-end',
        zIndex: layers.popoverInModal,
      }}
      messagesConfig={{
        placeholder: isEditing
          ? messages.placeholderEdit(t)
          : messages.placeholderCreate(t),
        buildingMessage: messages.building(t),
        successMessage: messages.successMessage(t),
        failureMessage: messages.failureMessage(t),
        initialMessage: isEditing
          ? messages.initialEdit(t)
          : messages.initialCreate(t),
      }}
      oneShotOnly={oneShotOnly}
    />
  );
};

export default AIEditSection;
